import { FormControl, Grid, PaperProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, UseFormReturn } from 'react-hook-form';
import { Dispatch, FC, SetStateAction, useCallback, useRef } from 'react';

import { CURRENCY_OPTIONS } from '@/constants';
import { useGetFactorsQuery, useGetForeignContractorsQuery } from '@/app/api';
import { useAppSelector } from '@/app/hooks';
import { Buyer } from '@/app/api/types';
import { BaseModalRef } from '@/app/helpers/types';

import UIAutocomplete from '@/shared/ui/ui-autocmplete';
import { UIFormLabel } from '@/shared/ui/ui-form-label';
import FormSelectInput from '@/shared/inputs/form-select-input';
import { FormNumericInput } from '@/shared/inputs/form-numeric-input';
import { FormDatePicker } from '@/shared/inputs/form-date-picker';

import { CreateSellerRequestValues } from '../types';
import { BuyerTinInput } from './inputs/buyer-tin-input';
import CreateContractorModal from './create-contractor-modal';
import { ForeignContractorsAutocompletePaper } from './foreign-contractors-autocomplete-paper';

const DEFAULT_DOCUMENTS: CreateSellerRequestValues['documents'] = [
  { value: null, type: '007', required: true },
  { value: null, type: '002', required: true, additionTypes: ['008'] },
  { value: null, type: '052', required: false },
  { value: null, type: '075', required: false },
];

const INTERNATIONAL_CURRENCY_OPTIONS = CURRENCY_OPTIONS.filter(
  (item) => item.value !== 'UZS'
);

interface CommonProps {
  form: UseFormReturn<CreateSellerRequestValues>;
  isInternationalFactoring: boolean;
}

type Props =
  | (CommonProps & {
      type: 'seller';
      buyersOptions: Buyer[];
      setBuyersOptions: Dispatch<SetStateAction<Buyer[]>>;
      isBuyersFetching: boolean;
    })
  | (CommonProps & {
      type: 'buyer';
      buyersOptions?: never;
      setBuyersOptions?: never;
      isBuyersFetching?: never;
    });

export const RenderDealRows: FC<Props> = (props) => {
  const {
    form,
    isInternationalFactoring,
    type,
    buyersOptions,
    setBuyersOptions,
    isBuyersFetching,
  } = props;
  const { t } = useTranslation('createDealForm');
  const { profile } = useAppSelector((state) => state.auth);
  const { setValue, control, watch } = form;

  const buyer = watch('buyer');
  const sellerName = watch('sellerName');
  const whoShouldPay = watch('isDebtor');
  const documents = watch('documents');
  const chosenFactor = watch('factor');
  const foreignContractorId = watch('foreignContractorId');
  const currency = watch('currency');

  const isClosedFactoring = whoShouldPay?.value === 1;
  const isContractAdded = documents?.some(
    (doc) => doc.type === '007' && doc.value
  );
  const isInvoiceAdded = documents?.some(
    (doc) => (doc.type === '002' || doc.type === '008') && doc.value
  );
  const hasSelectedInvoices = isInvoiceAdded && isContractAdded;

  const foreignContractorsModalRef = useRef<BaseModalRef>(null);

  const { data: foreignContractors, isFetching: isForeignContractorsFetching } =
    useGetForeignContractorsQuery(undefined, {
      skip: !isInternationalFactoring,
    });

  const { data: factors, isFetching: isFactorsFetching } = useGetFactorsQuery(
    isInternationalFactoring
      ? { international: true, currency: currency ?? undefined }
      : {
          companyTin:
            isClosedFactoring || type === 'buyer'
              ? profile?.tin || ''
              : buyer?.tin || '',
        },
    {
      skip: isInternationalFactoring
        ? !currency
        : type === 'seller'
          ? !buyer?.tin
          : !profile?.tin,
    }
  );

  const debtorOptions = [
    { value: 0, name: t('common:buyer') },
    { value: 1, name: t('common:supplier') },
  ];

  const renderForeignContractorsAutocompletePaper = useCallback(
    (props: PaperProps) => (
      <ForeignContractorsAutocompletePaper
        {...props}
        addContractorLabel={t('common:foreign_contractor.add')}
        onAddContractor={() => foreignContractorsModalRef.current?.open()}
      />
    ),
    [t]
  );

  const factorBranchSize =
    type === 'buyer' && !isInternationalFactoring ? 4 : 3;

  const renderInternationalPartnerField = () => {
    const selectedForeignContractor =
      foreignContractors?.data?.find(
        (item) => item.id === foreignContractorId
      ) ?? undefined;

    if (type === 'seller') {
      return (
        <UIAutocomplete
          value={selectedForeignContractor}
          options={foreignContractors?.data ?? []}
          loading={isForeignContractorsFetching}
          PaperComponent={renderForeignContractorsAutocompletePaper}
          getOptionLabel={(option) => `${option.name} (${option.tin})`}
          onChange={(_, value) => {
            setValue('foreignContractorId', value?.id);
            setValue(
              'buyer',
              value ? { name: value.name, tin: value.tin } : null
            );
            setValue('factor', null);
            setValue('documents', DEFAULT_DOCUMENTS);
          }}
          disableClearable
          TextFieldProps={{
            placeholder: t('common:not_selected'),
          }}
        />
      );
    }

    return (
      <UIAutocomplete
        value={selectedForeignContractor}
        options={foreignContractors?.data ?? []}
        loading={isForeignContractorsFetching}
        PaperComponent={renderForeignContractorsAutocompletePaper}
        getOptionLabel={(option) => `${option.name} (${option.tin})`}
        onChange={(_, value) => {
          setValue('foreignContractorId', value?.id);
          setValue('sellerTin', value?.tin ?? '');
          setValue('sellerName', value?.name ?? '');
        }}
        disableClearable
        TextFieldProps={{
          placeholder: t('common:not_selected'),
        }}
      />
    );
  };

  const renderPartnerRows = () => {
    if (isInternationalFactoring) {
      return (
        <>
          <Grid item xs={12} md={3}>
            <FormControl required>
              <UIFormLabel>{t('common:currency')}:</UIFormLabel>
              <Controller
                control={control}
                name='currency'
                rules={{
                  validate: (value) =>
                    INTERNATIONAL_CURRENCY_OPTIONS.some(
                      (item) => item.value === value
                    ) || t('errors:required_field'),
                }}
                render={({ field, fieldState }) => (
                  <UIAutocomplete
                    value={
                      INTERNATIONAL_CURRENCY_OPTIONS.find(
                        (item) => item.value === field.value
                      ) ?? null
                    }
                    options={INTERNATIONAL_CURRENCY_OPTIONS}
                    getOptionLabel={(option) => {
                      return t(`common:${option.label}`);
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    onChange={(_event, value) => {
                      field.onChange(value?.value ?? null);
                      setValue('factor', null);
                      setValue('branchId', null);
                    }}
                    onBlur={field.onBlur}
                    TextFieldProps={{
                      placeholder: t('common:not_selected'),
                      error: fieldState.invalid,
                      helperText: fieldState.error?.message,
                    }}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl required>
              <UIFormLabel>
                {type === 'seller'
                  ? t('common:buyers_tin')
                  : t('common:supplier_tin')}
                :
              </UIFormLabel>
              {renderInternationalPartnerField()}
            </FormControl>
          </Grid>
          <CreateContractorModal ref={foreignContractorsModalRef} />
        </>
      );
    }

    if (type === 'seller') {
      return (
        <>
          <Grid item xs={12} md={3}>
            <FormControl required>
              <UIFormLabel>{t('common:factor_will_paid_by')}:</UIFormLabel>
              <FormSelectInput
                control={control}
                name='isDebtor'
                options={debtorOptions}
                getOptionLabel={(o) => o.name}
                TextFieldProps={{
                  placeholder: t('common:not_selected'),
                }}
                rules={{ required: true }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl required>
              <UIFormLabel>{t('common:buyers_tin')}:</UIFormLabel>
              <BuyerTinInput
                form={form}
                options={buyersOptions ?? []}
                setOptions={setBuyersOptions!}
                isLoading={isBuyersFetching ?? false}
                onChange={() => {
                  setValue('factor', null);
                  setValue('documents', DEFAULT_DOCUMENTS);
                }}
                required
              />
            </FormControl>
          </Grid>
        </>
      );
    }

    return (
      <Grid item xs={12} md={4}>
        <FormControl required>
          <UIFormLabel>{t('common:supplier_tin')}:</UIFormLabel>
          <FormNumericInput
            control={control}
            name='sellerTin'
            thousandSeparator=''
            helperText={sellerName}
            rules={{
              required: true,
              pattern: {
                value: /^(?:\d{9}|\d{14})$/,
                message: t('errors:invalid_tin_format'),
              },
            }}
          />
        </FormControl>
      </Grid>
    );
  };

  return (
    <Grid container rowSpacing={1} columnSpacing={2}>
      {renderPartnerRows()}

      <Grid item xs={12} md={factorBranchSize}>
        <FormControl required>
          <UIFormLabel>{t('factor')}:</UIFormLabel>
          <FormSelectInput
            control={control}
            name='factor'
            options={factors?.data ?? []}
            loading={isFactorsFetching}
            getOptionLabel={(o) => `${o.name} (${o.tin})`}
            onChange={() => {
              setValue('branchId', null);
            }}
            TextFieldProps={{
              placeholder: t('common:not_selected'),
            }}
            rules={{ required: true }}
          />
        </FormControl>
      </Grid>

      <Grid item xs={12} md={factorBranchSize}>
        <FormControl>
          <UIFormLabel>{t('common:branch')}:</UIFormLabel>
          <FormSelectInput
            control={control}
            name='branchId'
            options={(chosenFactor && chosenFactor?.branches) || []}
            disabled={!chosenFactor?.branches?.length}
            loading={isFactorsFetching}
            getOptionLabel={(o) => `${o.name}`}
            TextFieldProps={{
              placeholder: t('common:not_selected'),
            }}
          />
        </FormControl>
      </Grid>

      <Grid item xs={12} md={4}>
        <FormControl required>
          <UIFormLabel>
            {isInternationalFactoring
              ? `${t('invoice_sum')}:`
              : `${t('invoice_based_sum')}, ${t(
                  'common:currency_options.UZS'
                ).toLowerCase()}:`}
          </UIFormLabel>
          <FormNumericInput
            control={control}
            name='contractAmount'
            rules={{ required: true }}
            thousandSeparator=' '
            disabled={hasSelectedInvoices}
            sx={{
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
              },
            }}
          />
        </FormControl>
      </Grid>

      <Grid item xs={12} md={4}>
        <FormControl required>
          <UIFormLabel>{t('buyer_debt_amount')}:</UIFormLabel>
          <FormNumericInput
            control={control}
            name='buyerDebtAmount'
            rules={{ required: true }}
            thousandSeparator=' '
          />
        </FormControl>
      </Grid>

      <Grid item xs={12} md={4}>
        <FormControl required>
          <UIFormLabel>{t('term_of_payment_contract')}</UIFormLabel>
          <FormDatePicker
            control={control}
            name='contractPaymentDueDate'
            format='DD.MM.YYYY'
            rules={{ required: true }}
          />
        </FormControl>
      </Grid>
    </Grid>
  );
};
